import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";

import { classNames } from "@/utils/classNames";

import { ContentStatus } from "../schema/knowledgeDb";
import { HandlingStrategy, Objection, ObjectionCategory } from "../schema/objectionHandling";

interface ObjectionContentAreaProps {
	categories: ObjectionCategory[];
	objections: Objection[];
	activeCategoryId: number | null;
	onObjectionChange: (updated: Objection) => void;
	onObjectionDelete: (objectionId: number) => void;
	onAddObjection: (categoryId: number) => void;
	onObjectionApprove: (objectionId: number) => void;
	onObjectionUnapprove: (objectionId: number) => void;
}

export const ObjectionContentArea: FunctionComponent<ObjectionContentAreaProps> = ({
	categories,
	objections,
	activeCategoryId,
	onObjectionChange,
	onObjectionDelete,
	onAddObjection,
	onObjectionApprove,
	onObjectionUnapprove,
}) => {
	const categoryRefs = useRef<Record<number, HTMLDivElement | null>>({});

	useEffect(() => {
		if (activeCategoryId && categoryRefs.current[activeCategoryId]) {
			categoryRefs.current[activeCategoryId]?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [activeCategoryId]);

	const objectionsByCategory = useMemo(() => {
		const map = new Map<number, Objection[]>();

		for (const cat of categories) {
			map.set(cat.id, []);
		}

		for (const obj of objections) {
			const list = map.get(obj.categoryId);

			if (list) {
				list.push(obj);
			}
		}

		return map;
	}, [categories, objections]);

	if (objections.length === 0) {
		return (
			<div className="flex-1 flex items-center justify-center text-sm text-gray-500 min-h-[400px]">
				No objections yet. Click &quot;+ Add Objection&quot; to get started.
			</div>
		);
	}

	return (
		<div className="flex-1 overflow-y-auto px-6">
			{categories.map((cat) => {
				const catObjections = objectionsByCategory.get(cat.id) ?? [];

				return (
					<div
						key={cat.id}
						ref={(el) => {
							categoryRefs.current[cat.id] = el;
						}}
						className="mb-10"
					>
						{/* Category header */}
						<div className="sticky top-0 z-10 bg-white pb-3 mb-4 border-b border-gray-200 py-4">
							<h2 className="text-ssmd font-bold text-gray-900">{cat.name}</h2>
							<p className="text-xs text-gray-500 mt-1">
								{catObjections.length} objection{catObjections.length === 1 ? "" : "s"}
							</p>
						</div>

						{/* Objection cards */}
						{catObjections.map((obj) => (
							<ObjectionCard
								key={obj.id}
								objection={obj}
								onApprove={() => onObjectionApprove(obj.id)}
								onChange={onObjectionChange}
								onDelete={() => onObjectionDelete(obj.id)}
								onUnapprove={() => onObjectionUnapprove(obj.id)}
							/>
						))}

						{/* + Add Objection to this category */}
						<button
							className="flex items-center text-sm font-medium text-brand-light hover:text-blue-action transition-colors cursor-pointer mt-2"
							type="button"
							onClick={() => onAddObjection(cat.id)}
						>
							+ Add Objection to this category
						</button>
					</div>
				);
			})}
		</div>
	);
};

/* ───────── Objection Card (collapsible) ───────── */

interface ObjectionCardProps {
	objection: Objection;
	onChange: (updated: Objection) => void;
	onDelete: () => void;
	onApprove: () => void;
	onUnapprove: () => void;
}

const ObjectionCard: FunctionComponent<ObjectionCardProps> = ({
	objection,
	onChange,
	onDelete,
	onApprove,
	onUnapprove,
}) => {
	const [isExpanded, setIsExpanded] = useState(true);
	const [isEditingConcern, setIsEditingConcern] = useState(false);
	const [isEditingQuote, setIsEditingQuote] = useState(false);
	const [editQuote, setEditQuote] = useState(objection.quote);
	const [editConcern, setEditConcern] = useState(objection.underlyingConcern);

	const autoApproveOnSave = () => {
		if (objection.status !== ContentStatus.HUMAN_APPROVED) {
			onApprove();
		}
	};

	const handleSaveQuote = () => {
		onChange({ ...objection, quote: editQuote });
		setIsEditingQuote(false);
		autoApproveOnSave();
	};

	const handleCancelQuote = () => {
		setEditQuote(objection.quote);
		setIsEditingQuote(false);
	};

	const handleSaveConcern = () => {
		onChange({ ...objection, underlyingConcern: editConcern });
		setIsEditingConcern(false);
		autoApproveOnSave();
	};

	const handleCancelConcern = () => {
		setEditConcern(objection.underlyingConcern);
		setIsEditingConcern(false);
	};

	const handleStrategyChange = (strategies: HandlingStrategy[]) => {
		onChange({ ...objection, strategies });
		autoApproveOnSave();
	};

	return (
		<div className="rounded-xl border border-gray-300 bg-white mb-4">
			{/* Header: quote + frequency + collapse */}
			<div className="flex items-start justify-between px-4 py-3 border-b border-gray-200">
				<div className="flex-1 min-w-0">
					{isEditingQuote ? (
						<div className="space-y-2">
							<textarea
								className="w-full min-h-[60px] px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
								value={editQuote}
								onChange={(e) => setEditQuote(e.target.value)}
							/>
							<div className="flex gap-2">
								<button
									className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
									type="button"
									onClick={handleCancelQuote}
								>
									Cancel
								</button>
								<button
									className="px-3 py-1.5 text-xs font-medium text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors"
									type="button"
									onClick={handleSaveQuote}
								>
									Save
								</button>
							</div>
						</div>
					) : (
						<button
							className="text-left cursor-pointer"
							type="button"
							onClick={() => {
								setEditQuote(objection.quote);
								setIsEditingQuote(true);
							}}
						>
							<h3 className="text-sm font-semibold text-gray-900 hover:text-brand-light transition-colors">
								&ldquo;{objection.quote}&rdquo;
							</h3>
						</button>
					)}
				</div>

				<div className="flex items-center gap-2 ml-3 shrink-0">
					{objection.status === ContentStatus.AI_DRAFTED && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-white border border-blue-action/40 text-blue-action">
							✨ AI
						</span>
					)}
					{objection.status === ContentStatus.DRAFT && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-warning-light border border-status-warning/40 text-status-warning">
							✎ Draft
						</span>
					)}
					{objection.status === ContentStatus.HUMAN_APPROVED && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-success-light border border-status-success/40 text-status-success">
							✓ Approved
						</span>
					)}
					{!isEditingQuote && (
						<>
							{(objection.status === ContentStatus.AI_DRAFTED ||
								objection.status === ContentStatus.DRAFT) && (
								<button
									className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={onApprove}
								>
									✓ Approve
								</button>
							)}
							{objection.status === ContentStatus.HUMAN_APPROVED && (
								<button
									className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={onUnapprove}
								>
									✗ Unapprove
								</button>
							)}
							<button
								className="text-xs font-medium cursor-pointer text-text-meta hover:text-blue-action px-2 py-1 rounded transition-colors"
								type="button"
								onClick={() => {
									setEditQuote(objection.quote);
									setIsEditingQuote(true);
								}}
							>
								Edit
							</button>
							<button
								className="text-xs font-medium cursor-pointer text-red-500 hover:text-red-500/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onDelete}
							>
								Delete
							</button>
						</>
					)}
					<button
						className="text-xs font-medium text-gray-500 hover:text-gray-700 px-1 py-1 transition-colors cursor-pointer"
						type="button"
						onClick={() => setIsExpanded((prev) => !prev)}
					>
						{isExpanded ? "▲ Collapse" : "▼ Expand"}
					</button>
				</div>
			</div>

			{/* Expanded body */}
			{isExpanded && (
				<div className="px-4 py-3 space-y-4">
					{/* Underlying Concern */}
					<div className="rounded-lg border border-gray-200 bg-brand-white p-3">
						<div className="flex items-center justify-between mb-1">
							<span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
								Underlying Concern
							</span>
							{!isEditingConcern && (
								<button
									className="text-xs font-medium cursor-pointer text-text-meta hover:text-blue-action transition-colors"
									type="button"
									onClick={() => {
										setEditConcern(objection.underlyingConcern);
										setIsEditingConcern(true);
									}}
								>
									Edit
								</button>
							)}
						</div>
						{isEditingConcern ? (
							<div className="space-y-2">
								<textarea
									className="w-full min-h-[60px] px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light bg-white"
									value={editConcern}
									onChange={(e) => setEditConcern(e.target.value)}
								/>
								<div className="flex justify-end gap-2">
									<button
										className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
										type="button"
										onClick={handleCancelConcern}
									>
										Cancel
									</button>
									<button
										className="px-3 py-1.5 text-xs font-medium text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors"
										type="button"
										onClick={handleSaveConcern}
									>
										Save
									</button>
								</div>
							</div>
						) : (
							<p className="text-sm text-gray-700 leading-relaxed">
								{objection.underlyingConcern || (
									<span className="text-gray-400 italic">No concern documented.</span>
								)}
							</p>
						)}
					</div>

					{/* Handling Strategies */}
					<HandlingStrategiesSection
						strategies={objection.strategies}
						onSave={handleStrategyChange}
					/>
				</div>
			)}
		</div>
	);
};

/* ───────── Handling Strategies (FeatureBenefitTable pattern) ───────── */

interface HandlingStrategiesSectionProps {
	strategies: HandlingStrategy[];
	onSave: (strategies: HandlingStrategy[]) => void;
}

interface StrategiesForm {
	strategies: HandlingStrategy[];
}

const HandlingStrategiesSection: FunctionComponent<HandlingStrategiesSectionProps> = ({
	strategies,
	onSave,
}) => {
	const [isEditing, setIsEditing] = useState(false);

	const { control, register, handleSubmit, reset } = useForm<StrategiesForm>({
		defaultValues: { strategies },
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "strategies",
	});

	const handleEdit = () => {
		reset({ strategies });
		setIsEditing(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		reset({ strategies });
	};

	const onSubmit = (data: StrategiesForm) => {
		onSave(data.strategies);
		setIsEditing(false);
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-2">
				<span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
					Handling Strategies
				</span>
				{!isEditing && (
					<button
						className="text-xs font-medium cursor-pointer text-text-meta hover:text-blue-action px-2 py-1 rounded transition-colors"
						type="button"
						onClick={handleEdit}
					>
						Edit
					</button>
				)}
			</div>

			{isEditing ? (
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-3">
						{fields.map((field, index) => (
							<div
								key={field.id}
								className="rounded-lg border border-gray-200 bg-brand-white p-3 relative"
							>
								<button
									className="absolute top-2 right-2 text-red-500 hover:text-red-500/80 text-xs cursor-pointer"
									type="button"
									onClick={() => remove(index)}
								>
									✕
								</button>
								<div className="space-y-2 pr-6">
									<div>
										<label className="block text-xs font-medium text-gray-600 mb-1">
											Strategy Name
										</label>
										<input
											className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
											type="text"
											{...register(`strategies.${index}.strategyName`)}
										/>
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-600 mb-1">Approach</label>
										<textarea
											className="w-full min-h-[50px] px-3 py-1.5 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
											{...register(`strategies.${index}.approach`)}
										/>
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-600 mb-1">
											Example Reply
										</label>
										<textarea
											className="w-full min-h-[50px] px-3 py-1.5 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
											{...register(`strategies.${index}.exampleReply`)}
										/>
									</div>
								</div>
							</div>
						))}
					</div>
					<div className="flex justify-between mt-3">
						<button
							className="text-xs font-medium text-brand-light hover:text-blue-action transition-colors cursor-pointer"
							type="button"
							onClick={() =>
								append({
									id: Date.now(),
									strategyName: "",
									approach: "",
									exampleReply: "",
								})
							}
						>
							+ Add strategy
						</button>
						<div className="flex gap-2">
							<button
								className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
								type="button"
								onClick={handleCancel}
							>
								Cancel
							</button>
							<button
								className="px-3 py-1.5 text-xs font-medium text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors"
								type="submit"
							>
								Save
							</button>
						</div>
					</div>
				</form>
			) : (
				<ReadOnlyStrategies strategies={strategies} />
			)}
		</div>
	);
};

/* ───────── Read-only strategy display ───────── */

const ReadOnlyStrategies: FunctionComponent<{ strategies: HandlingStrategy[] }> = ({
	strategies,
}) => {
	if (strategies.length === 0) {
		return <p className="text-sm text-gray-400 italic">No handling strategies yet.</p>;
	}

	return (
		<div className="space-y-4">
			{strategies.map((s) => (
				<div key={s.id}>
					<p className="text-sm font-semibold text-gray-900 mb-1">{s.strategyName}</p>
					<p className="text-sm text-gray-700 leading-relaxed mb-2">{s.approach}</p>
					{s.exampleReply && (
						<div className="border-l-4 border-indigo-500 pl-4 py-2">
							<p className="text-sm text-gray-700 italic leading-relaxed">
								&ldquo;{s.exampleReply}&rdquo;
							</p>
						</div>
					)}
				</div>
			))}
		</div>
	);
};
